// A stale tab still runs the index.html of the previous deploy. The UI deploy prunes the old
// hashed chunks, so the first lazy route that tab opens 404s. Reloading fetches the new
// index.html and its chunks; the sessionStorage guard stops a genuinely missing chunk from
// looping.

const STORAGE_KEY = 'chunk-error-reload';
const RELOAD_COOLDOWN_MS = 10000;
const MAX_RELOADS = 3;

const CHUNK_ERROR_PATTERNS = [
  'Failed to fetch dynamically imported module', // Chromium
  'Importing a module script failed', // Safari
  'error loading dynamically imported module', // Firefox
  'Unable to preload CSS', // Vite CSS preload
];

export function isChunkLoadError(error: unknown): boolean {
  const message =
    error instanceof Error ? error.message : typeof error === 'string' ? error : '';
  return CHUNK_ERROR_PATTERNS.some((pattern) => message.includes(pattern));
}

/**
 * Reloads the page if the reload budget allows it. Returns true when a reload was triggered.
 */
let reloadPending = false;

export function reloadForChunkError(): boolean {
  // One failed import usually surfaces through several listeners (vite:preloadError, then
  // the route errorElement); all of them should see the reload that is already in flight.
  if (reloadPending) {
    return true;
  }

  let lastReloadTime = 0;
  let reloadCount = 0;
  try {
    const parsed = JSON.parse(sessionStorage.getItem(STORAGE_KEY) ?? '{}');
    lastReloadTime = parsed.time || 0;
    reloadCount = parsed.count || 0;
  } catch {
    // Invalid data, start over
  }

  const now = Date.now();
  const canReload =
    reloadCount === 0 || (now - lastReloadTime > RELOAD_COOLDOWN_MS && reloadCount < MAX_RELOADS);
  if (!canReload) {
    return false;
  }

  sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ time: now, count: reloadCount + 1 }));
  reloadPending = true;
  window.location.reload();
  return true;
}
